/**
 * What the lobby hands to the call, and what survives a page reload.
 *
 * Four separate concerns, deliberately in one file because they are the same
 * seam — everything that has to cross from `/` to `/call/$code` — and because
 * each of them has a different correct lifetime:
 *
 *   the GRANT      sessionStorage. Per tab, carries the lobby handoff and
 *                  creator authority. After one successful media connection,
 *                  it becomes a one-time resume credential: reload exchanges
 *                  it for a fresh LiveKit token/QoE UUID while preserving the
 *                  participant identity and cumulative transcript lane.
 *   the PREFLIGHT  memory only. It is a live `MediaStream` and there is no
 *                  serialisation of a camera.
 *   the NAME       localStorage. The one thing a returning visitor should not
 *                  have to type again.
 *   the CALL LEASE Web Locks plus an expiring localStorage heartbeat. A grant
 *                  is per tab, but one browser must not accidentally publish
 *                  from two tabs at once.
 *
 * No LiveKit import here on purpose. This module is loaded by the landing
 * page, and `livekit-client` is ~400 KB that nobody on the landing page needs
 * yet (see the `manualChunks` comment in `vite.config.ts`).
 */

import type { JoinGrant } from '@era/shared'

/* -------------------------------------------------------------------------- */
/* the grant                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The complete grant returned by the BFF. A creator grant includes an opaque
 * `control_token`; a joiner grant does not. There is deliberately no separate
 * `isCreator` flag: a browser boolean is not authorization, while possession
 * of the capability is both what enables the UI and what the BFF verifies.
 */
export interface HeldGrant {
  grant: JoinGrant
  /** Set after one successful Room.connect; creator authority remains readable. */
  connected?: boolean
  /** Durable-in-this-tab request material for one exact lost-response retry. */
  pending_resume?: PendingResumeExchange
}

export interface PendingResumeExchange {
  prior_resume_capability_id: string
  request_id: string
  successor_resume_capability_id: string
  successor_resume_capability: string
  successor_quality_connection_id: string
  successor_quality_capability: string
}

const GRANT_PREFIX = 'era.meet.grant.'
const NAME_KEY = 'era.meet.name'
const CONTROL_TOKEN = /^[A-Za-z0-9_-]{43}$/
const QUALITY_CONNECTION = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

/* -------------------------------------------------------------------------- */
/* one active call per browser                                                  */
/* -------------------------------------------------------------------------- */

const ACTIVE_CALL_KEY = 'era.meet.active-call'
const ACTIVE_CALL_LOCK = 'era.meet.active-call.lock'
const ACTIVE_CALL_TTL_MS = 6_000
const ACTIVE_CALL_HEARTBEAT_MS = 2_000
const ACTIVE_CALL_CLAIM_WINDOW_MS = 100

interface StoredCallLease {
  owner: string
  sessionId: string
  expiresAt: number
}

export interface ActiveCallLease {
  /** Stop heartbeats, remove our record and release the browser lock. Idempotent. */
  release: () => void
  /** Called if another tab supersedes this lease. */
  onLost: (callback: () => void) => () => void
}

export type ActiveCallLeaseResult =
  | { ok: true; lease: ActiveCallLease }
  | { ok: false; reason: 'busy' | 'unavailable' | 'aborted' }

interface BrowserLockGate {
  release: () => void
}

/**
 * Claim the same-origin right to join and publish one call.
 *
 * Web Locks is the atomic arbitration path. The expiring localStorage record
 * is both a fallback for browsers without Web Locks and the crash-safe
 * heartbeat: a tab that disappears can block another tab for at most six
 * seconds. The fallback verifies its owner after a short claim window and
 * listens for later overwrites, so simultaneous writes elect the last record
 * rather than allowing both claimants to continue.
 */
export async function acquireActiveCallLease(
  sessionId: string,
  signal?: AbortSignal,
): Promise<ActiveCallLeaseResult> {
  if (signal?.aborted) return { ok: false, reason: 'aborted' }

  const lock = await acquireBrowserLock(signal)
  if (lock === null) return { ok: false, reason: signal?.aborted ? 'aborted' : 'busy' }

  const storage = callLeaseStorageAvailable()
  // Web Locks alone is still an atomic, crash-releasing lease. Without either
  // primitive there is no honest way to promise that another tab cannot join.
  if (!storage && lock === undefined) return { ok: false, reason: 'unavailable' }

  const owner = crypto.randomUUID()
  let released = false
  let heartbeat: number | null = null
  const lostCallbacks = new Set<() => void>()

  const removeOurRecord = () => {
    if (!storage) return
    const current = readStoredCallLease()
    if (current?.owner !== owner) return
    try {
      localStorage.removeItem(ACTIVE_CALL_KEY)
    } catch {
      /* Web Locks still owns the atomic lease when storage disappears. */
    }
  }

  const release = () => {
    if (released) return
    released = true
    if (heartbeat !== null) window.clearInterval(heartbeat)
    window.removeEventListener('storage', handleStorage)
    window.removeEventListener('pagehide', release)
    removeOurRecord()
    lock?.release()
    lostCallbacks.clear()
  }

  const lose = () => {
    if (released) return
    for (const callback of [...lostCallbacks]) callback()
    release()
  }

  function handleStorage(event: StorageEvent) {
    if (event.key !== ACTIVE_CALL_KEY || released) return
    if (readStoredCallLease()?.owner !== owner) lose()
  }

  if (storage) {
    const current = readStoredCallLease()
    if (current && current.owner !== owner && current.expiresAt > Date.now()) {
      lock?.release()
      return { ok: false, reason: 'busy' }
    }

    writeStoredCallLease({ owner, sessionId, expiresAt: Date.now() + ACTIVE_CALL_TTL_MS })
    const settled = await waitForClaimWindow(signal)
    if (!settled || readStoredCallLease()?.owner !== owner) {
      removeOurRecord()
      lock?.release()
      return { ok: false, reason: signal?.aborted ? 'aborted' : 'busy' }
    }

    heartbeat = window.setInterval(() => {
      const currentLease = readStoredCallLease()
      if (currentLease?.owner !== owner) {
        lose()
        return
      }
      writeStoredCallLease({ owner, sessionId, expiresAt: Date.now() + ACTIVE_CALL_TTL_MS })
    }, ACTIVE_CALL_HEARTBEAT_MS)
    window.addEventListener('storage', handleStorage)
  }

  window.addEventListener('pagehide', release)

  return {
    ok: true,
    lease: {
      release,
      onLost(callback) {
        if (released) {
          callback()
          return () => undefined
        }
        lostCallbacks.add(callback)
        return () => lostCallbacks.delete(callback)
      },
    },
  }
}

/** `undefined` means unsupported, `null` means another tab owns the lock. */
async function acquireBrowserLock(
  signal?: AbortSignal,
): Promise<BrowserLockGate | null | undefined> {
  if (!navigator.locks?.request) return undefined

  return new Promise((resolve) => {
    let answered = false
    let unlock: () => void = () => undefined
    const held = new Promise<void>((release) => {
      unlock = release
    })

    void navigator.locks
      .request(
        ACTIVE_CALL_LOCK,
        { mode: 'exclusive', ifAvailable: true, ...(signal ? { signal } : {}) },
        async (lock) => {
          answered = true
          if (!lock) {
            resolve(null)
            return
          }
          resolve({ release: unlock })
          await held
        },
      )
      .catch(() => {
        if (!answered) resolve(signal?.aborted ? null : undefined)
      })
  })
}

function callLeaseStorageAvailable(): boolean {
  try {
    const probe = `${ACTIVE_CALL_KEY}.probe`
    localStorage.setItem(probe, '1')
    localStorage.removeItem(probe)
    return true
  } catch {
    return false
  }
}

function readStoredCallLease(): StoredCallLease | null {
  try {
    const raw = localStorage.getItem(ACTIVE_CALL_KEY)
    if (!raw) return null
    const value = JSON.parse(raw) as Partial<StoredCallLease>
    if (typeof value.owner !== 'string' || typeof value.sessionId !== 'string') return null
    if (typeof value.expiresAt !== 'number' || !Number.isFinite(value.expiresAt)) return null
    return value as StoredCallLease
  } catch {
    return null
  }
}

function writeStoredCallLease(value: StoredCallLease): void {
  try {
    localStorage.setItem(ACTIVE_CALL_KEY, JSON.stringify(value))
  } catch {
    // The next heartbeat will notice that ownership can no longer be proven.
  }
}

function waitForClaimWindow(signal?: AbortSignal): Promise<boolean> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve(false)
      return
    }
    const timer = window.setTimeout(done, ACTIVE_CALL_CLAIM_WINDOW_MS)
    signal?.addEventListener('abort', aborted, { once: true })

    function done() {
      signal?.removeEventListener('abort', aborted)
      resolve(true)
    }
    function aborted() {
      window.clearTimeout(timer)
      resolve(false)
    }
  })
}

/**
 * How much of the token's life must be left for it to be worth trying.
 *
 * `expires_at` bounds JOINING only — the spec is explicit that an established
 * connection is unaffected — so this margin protects exactly one moment: the
 * `room.connect` call. Thirty seconds covers a slow handshake without
 * discarding a token that would have worked.
 */
const EXPIRY_MARGIN_MS = 30_000

export function stashGrant(held: HeldGrant): void {
  try {
    sessionStorage.setItem(GRANT_PREFIX + held.grant.join_code, JSON.stringify(held))
  } catch {
    // Private-browsing quota, or storage disabled. The current call still
    // works, but this tab cannot prove which participant identity to resume
    // after a reload. The UI never silently invents a replacement identity.
  }
}

/**
 * The stashed grant for this code, or null if there is none or an unused
 * LiveKit token is too close to expiry to use.
 *
 * Returning null is not an error: the caller's response is to ask the server
 * for a fresh grant, which is a normal path and not a fallback.
 */
export function readGrant(code: string): HeldGrant | null {
  let raw: string | null = null
  try {
    raw = sessionStorage.getItem(GRANT_PREFIX + code)
  } catch {
    return null
  }
  if (!raw) return null

  let held: HeldGrant
  try {
    held = JSON.parse(raw) as HeldGrant
  } catch {
    return null
  }

  // Shape check rather than trust: this came out of storage, which a previous
  // version of this app also wrote to.
  const grant = held?.grant
  if (!grant || typeof grant.url !== 'string' || typeof grant.token !== 'string') return null
  if (typeof grant.session_id !== 'string' || typeof grant.identity !== 'string') return null
  // A grant written by a pre-QoE build is intentionally re-minted. Reusing its
  // LiveKit token would connect successfully but create a session with no
  // browser-quality capability, silently defeating the coverage gate.
  if (
    typeof grant.quality_token !== 'string' || !CONTROL_TOKEN.test(grant.quality_token) ||
    typeof grant.quality_connection_id !== 'string' ||
    !QUALITY_CONNECTION.test(grant.quality_connection_id)
  ) return null
  // Resume authority is deliberately separate from the QoE bearer. A grant
  // from a build that predates it cannot safely preserve identity on reload.
  if (
    typeof grant.resume_capability !== 'string' ||
    !CONTROL_TOKEN.test(grant.resume_capability) ||
    typeof grant.resume_capability_id !== 'string' ||
    !QUALITY_CONNECTION.test(grant.resume_capability_id)
  ) return null
  // A connected grant is not reused for LiveKit or QoE. It is returned only so
  // CallRoute can exchange its QoE bearer once for a fresh token/UUID while
  // preserving the server-stored participant identity. Its LiveKit expiry is
  // irrelevant to that exchange; the BFF checks the independently bounded QoE
  // capability expiry.
  if (held.connected) return { ...held, grant, connected: true }

  const expiry = Date.parse(grant.expires_at)
  if (Number.isFinite(expiry) && expiry - Date.now() < EXPIRY_MARGIN_MS) {
    // Keep an expired creator grant in sessionStorage long enough to carry its
    // room-control capability across minting a fresh LiveKit participant token.
    // A join response never contains that capability and must not erase one
    // this tab already proved it possessed for the same session.
    if (!validControlToken(grant.control_token)) clearGrant(code)
    return null
  }

  return { ...held, grant, connected: false }
}

/** True when this stored grant must be exchanged before Room.connect. */
export function grantNeedsResume(held: HeldGrant): boolean {
  return held.connected === true
}

/**
 * Freeze browser-proposed successor secrets before the network request.
 *
 * If the success response is lost, sessionStorage still holds this exact
 * request. The BFF can compare hashes/IDs and remint only the LiveKit JWT; it
 * never needs to persist plaintext successor authority.
 */
export function prepareGrantResume(code: string, held: HeldGrant): HeldGrant {
  if (!grantNeedsResume(held)) return held
  if (held.grant.join_code !== code) throw new Error('resume grant does not match this join code')
  const existing = held.pending_resume
  if (existing && validPendingResume(existing, held.grant.resume_capability_id)) return held

  const pending: PendingResumeExchange = {
    prior_resume_capability_id: held.grant.resume_capability_id,
    request_id: crypto.randomUUID(),
    successor_resume_capability_id: crypto.randomUUID(),
    successor_resume_capability: randomBrowserCapability(),
    successor_quality_connection_id: crypto.randomUUID(),
    successor_quality_capability: randomBrowserCapability(),
  }
  const prepared = { ...held, pending_resume: pending }
  stashGrant(prepared)
  return prepared
}

/**
 * Mark one LiveKit/QoE grant as requiring an authenticated resume.
 *
 * Called only after `Room.connect` succeeds. A reload sends this grant's QoE
 * UUID/bearer to the resume branch of the join endpoint and receives a fresh
 * LiveKit token plus fresh QoE UUID for the SAME stored participant identity.
 * `readCreatorControl` still recovers creator authority for that session.
 */
export function markGrantConnected(
  code: string,
  sessionId: string,
  qualityConnectionId: string,
): void {
  try {
    const raw = sessionStorage.getItem(GRANT_PREFIX + code)
    if (!raw) return
    const held = JSON.parse(raw) as HeldGrant
    if (
      held?.grant?.session_id !== sessionId ||
      held.grant.quality_connection_id !== qualityConnectionId
    ) return
    sessionStorage.setItem(GRANT_PREFIX + code, JSON.stringify({ ...held, connected: true }))
  } catch {
    // Storage being unavailable already means the page cannot reuse the grant.
  }
}

/**
 * Recover a creator capability across LiveKit token refresh in the same tab.
 * It is accepted only for the same join code AND session id; a join response
 * for another room can never inherit it.
 */
export function readCreatorControl(code: string, sessionId: string): string | null {
  let raw: string | null = null
  try {
    raw = sessionStorage.getItem(GRANT_PREFIX + code)
  } catch {
    return null
  }
  if (!raw) return null

  try {
    const held = JSON.parse(raw) as HeldGrant
    const grant = held?.grant
    if (grant?.session_id !== sessionId || !validControlToken(grant.control_token)) return null
    return grant.control_token
  } catch {
    return null
  }
}

/** Mark a refused media token unusable without discarding creator authority. */
export function invalidateLiveGrant(code: string): void {
  let raw: string | null = null
  try {
    raw = sessionStorage.getItem(GRANT_PREFIX + code)
    if (!raw) return
    const held = JSON.parse(raw) as HeldGrant
    if (!held?.grant) return
    stashGrant({
      ...held,
      connected: false,
      pending_resume: undefined,
      grant: { ...held.grant, expires_at: new Date(0).toISOString() },
    })
  } catch {
    clearGrant(code)
  }
}

export function clearGrant(code: string): void {
  try {
    sessionStorage.removeItem(GRANT_PREFIX + code)
  } catch {
    /* nothing to clean up if storage is unavailable */
  }
}

function validControlToken(value: unknown): value is string {
  return typeof value === 'string' && CONTROL_TOKEN.test(value)
}

function validPendingResume(value: PendingResumeExchange, priorId: string): boolean {
  return (
    value.prior_resume_capability_id === priorId &&
    QUALITY_CONNECTION.test(value.request_id) &&
    QUALITY_CONNECTION.test(value.successor_resume_capability_id) &&
    CONTROL_TOKEN.test(value.successor_resume_capability) &&
    QUALITY_CONNECTION.test(value.successor_quality_connection_id) &&
    CONTROL_TOKEN.test(value.successor_quality_capability)
  )
}

function randomBrowserCapability(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32))
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '')
}

/* -------------------------------------------------------------------------- */
/* the name                                                                    */
/* -------------------------------------------------------------------------- */

export function recallName(): string {
  try {
    return localStorage.getItem(NAME_KEY) ?? ''
  } catch {
    return ''
  }
}

export function rememberName(name: string): void {
  try {
    localStorage.setItem(NAME_KEY, name.trim())
  } catch {
    /* the field simply starts empty next time */
  }
}

/* -------------------------------------------------------------------------- */
/* the host capability                                                         */
/* -------------------------------------------------------------------------- */

/**
 * The appointment host token, kept exactly like `control_token`: per TAB, in
 * sessionStorage, and never in a URL.
 *
 * sessionStorage rather than localStorage is the deliberate choice and it has
 * a cost worth naming — close the tab and the host cannot start that meeting
 * again. That is the right trade for a capability that spends upstream
 * capacity: a token in localStorage outlives the session on a shared or public
 * machine, and the recovery from losing it (create another appointment, send
 * the new link) is cheap. It is also why `AppointmentGrant` is shown to the
 * host once with an explicit "save this link" affordance rather than being
 * treated as recoverable state.
 *
 * NEVER put this in the URL. A slug in a link is fine — it reaches a lobby and
 * nothing else — but a host token in a query string lands in browser history,
 * the Referer header, and every log between here and the server.
 */
const HOST_TOKEN_PREFIX = 'era.meet.host.'

export function stashHostToken(slug: string, token: string): void {
  try {
    sessionStorage.setItem(HOST_TOKEN_PREFIX + slug, token)
  } catch {
    // Storage disabled. The host can still start the meeting in THIS tab from
    // the value held in memory; they simply cannot reload and resume as host.
  }
}

export function readHostToken(slug: string): string | null {
  try {
    const token = sessionStorage.getItem(HOST_TOKEN_PREFIX + slug)
    return token && CONTROL_TOKEN.test(token) ? token : null
  } catch {
    return null
  }
}

export function forgetHostToken(slug: string): void {
  try {
    sessionStorage.removeItem(HOST_TOKEN_PREFIX + slug)
  } catch {
    /* nothing to do */
  }
}

/**
 * The shared-password host session, which is a DIFFERENT credential from the
 * per-appointment token above and is stored separately on purpose.
 *
 * The per-appointment token authorizes one meeting; this authorizes any
 * meeting in this deployment. Keeping them in one slot would make it easy to
 * write code that does not care which one it has, and the whole point is that
 * the difference matters.
 *
 * sessionStorage again, not localStorage. A shared password's session is
 * exactly the thing that should not outlive the tab on a borrowed machine, and
 * the server's signing key is ephemeral anyway, so a restart invalidates it
 * regardless of what the browser kept.
 */
const HOST_SESSION_KEY = 'era.meet.host-session'

interface StoredHostSession {
  token: string
  expires_at: string
}

export function stashHostSession(session: StoredHostSession): void {
  try {
    sessionStorage.setItem(HOST_SESSION_KEY, JSON.stringify(session))
  } catch {
    /* the host simply signs in again */
  }
}

/** The stored session, or null if absent, malformed, or expired. */
export function readHostSession(): string | null {
  let raw: string | null = null
  try {
    raw = sessionStorage.getItem(HOST_SESSION_KEY)
  } catch {
    return null
  }
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<StoredHostSession>
    if (typeof parsed.token !== 'string' || typeof parsed.expires_at !== 'string') return null
    // Client-side expiry is a COURTESY, not a check — it stops the UI offering
    // a start button that is certain to 401. The server re-verifies the
    // signature and the expiry on every use and is the only authority.
    if (Date.parse(parsed.expires_at) <= Date.now()) {
      forgetHostSession()
      return null
    }
    return parsed.token
  } catch {
    return null
  }
}

export function forgetHostSession(): void {
  try {
    sessionStorage.removeItem(HOST_SESSION_KEY)
  } catch {
    /* nothing to do */
  }
}

/* -------------------------------------------------------------------------- */
/* the preflight media                                                         */
/* -------------------------------------------------------------------------- */

/**
 * The camera and microphone the lobby already opened, handed to the call.
 *
 * This exists so that the light on the camera does not blink off and on
 * between "here is what you look like" and "you are in the call". It is the
 * single most noticeable difference between a call app that feels considered
 * and one that does not.
 *
 * `stream` is OWNED by whoever last called `takePreflight`. The lobby stops
 * stopping it the moment it hands it over, and the call stops it if the
 * connection fails. Exactly one owner at a time, or the published track dies
 * when the lobby unmounts.
 */
export interface PreflightMedia {
  stream: MediaStream
  /** What the toggles in the lobby were set to. Carried into the call as-is. */
  cameraEnabled: boolean
  micEnabled: boolean
  /** The devices the person actually chose, so the call does not re-guess. */
  cameraDeviceId: string | null
  micDeviceId: string | null
}

let preflight: PreflightMedia | null = null
/**
 * The last thing handed out, kept so a SECOND take returns the same object.
 *
 * React's StrictMode double-invokes component bodies and effects in
 * development. A strictly destructive take would therefore hand the camera to
 * the first invocation and `null` to the second — the one that actually
 * renders — and the handoff would appear to work in production and silently
 * not work while anyone was developing it.
 *
 * The consumer is required to check `readyState` before publishing anyway
 * (`publishPreflight` in `use-room.ts` does), so a stale hand-back degrades to
 * "acquire the devices normally" rather than to a black tile.
 */
let claimed: PreflightMedia | null = null

export function stashPreflight(media: PreflightMedia): void {
  // Anything still held from an abandoned attempt is stopped, not leaked: a
  // MediaStream nobody references still holds the camera open.
  if (preflight && preflight.stream !== media.stream) stopStream(preflight.stream)
  preflight = media
  claimed = null
}

/** Takes ownership. Idempotent — see the note on `claimed`. */
export function takePreflight(): PreflightMedia | null {
  if (preflight) {
    claimed = preflight
    preflight = null
  }
  return claimed
}

export function discardPreflight(): void {
  if (preflight) stopStream(preflight.stream)
  preflight = null
  claimed = null
}

/**
 * Forget the stash WITHOUT stopping anything.
 *
 * The counterpart to `discardPreflight`, for the case where the handoff is
 * cancelled and the original holder is taking its tracks back — a join that
 * failed, leaving the lobby's preview still on screen and still live.
 */
export function abandonPreflight(): void {
  preflight = null
  claimed = null
}

export function stopStream(stream: MediaStream): void {
  for (const track of stream.getTracks()) track.stop()
}
